import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Divider,
  Grid,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { AddRounded, Circle } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { RHFCheckbox, RHFRadioGroup, RHFSelect, RHFTextField } from '../../../../components/hook-form';
import { countries } from '../../../../_mock';
import Iconify from '../../../../components/Iconify';
import UploadPhoto from '../../../../sections/giving_basket/UploadPhoto';
import NoAmountModal from '../NoAmountModal';
import { convertImagesToBase64STring } from '../../../../utils/resizeFile';
import { DonationAPI } from '../../../../services/donation.services';
import { DONATION_TYPE } from '../../../../config';

const GOODS_OPTIONS = [
  'clothes',
  'blanket/ linen',
  'books',
  'school supplies',
  'food',
  'equipment',
  'furniture',
  'hygiene essentials',
  'medicines/medical supplies',
  'others, specify',
];

SimplifiedGoodsForm.propTypes = {
  projects: PropTypes.array.isRequired,
  onSuccess: PropTypes.func,
  actionRef: PropTypes.any,
  resolverRef: PropTypes.any,
  methods: PropTypes.any,
  setIsLoading: PropTypes.func,
  isDiscretionary: PropTypes.bool,
};

export default function SimplifiedGoodsForm({
  projects,
  onSuccess,
  actionRef,
  resolverRef,
  methods,
  setIsLoading,
  isDiscretionary,
}) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [noAmountOpen, setNoAmountOpen] = useState(false);

  const options = [
    'Unit 204, 2/F Vivaldi Condominium, 628 EDSA, Cubao, Quezon City',
    '5D Pinatubo St., Bgy. San Martin de Porres, Cubao, Quezon City',
  ];

  const channels = [
    {
      text: 'Call us via out landline number at  +63 28',
    },
    {
      text: 'Message or call us via our mobile number +63',
    },
    {
      text: 'Email us at contact@charityphilippines.org',
    },
    {
      text: 'Or fill up the donation form below then we will connect with you',
    },
  ];

  const resolver = yupResolver(
    Yup.object().shape({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().required('Required'),
      address: Yup.string().required('Required'),
      country: Yup.string().required('Required'),
      phone: Yup.string().required('Required'),
      donationAmount: Yup.string().required('Required'),
      file: Yup.array().min(1, 'Please select a photo').required('Required'),
      items: Yup.array().of(
        Yup.object().shape({
          goods: Yup.string().required('Required'),
          quantity: Yup.number().required('Required'),
        })
      ),
      dropAddress: Yup.string().when('pickup', {
        is: false,
        then: Yup.string().required('Required'),
      }),
      pickupAddress: Yup.string().when('pickup', {
        is: true,
        then: Yup.string().required('Required'),
      }),
    })
  );

  const {
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const values = watch();

  const defaultValues = {
    items: [{}],
    country: 'PH',
    dropAddress: options.at(0),
  };

  useEffect(() => {
    setValue('file', selectedImages);
  }, [selectedImages]);

  useEffect(() => {
    resolverRef.current = resolver;
    reset(defaultValues);
    actionRef.current = onSubmitForm;
  }, []);

  console.log(values);

  const addItem = () => {
    setValue('items', [...values.items, {}]);
  };

  const handleRemove = (index) => {
    const items = [...values.items];
    items.splice(index, 1);
    setValue('items', items);
  };

  const onSubmitForm = async (data, projects) => {
    data.donationAmount = 0;

    setIsLoading(true);
    const payload = await createPayload({
      discretionary: isDiscretionary,
      formData: data,
      projects,
      images: data.file,
    });
    DonationAPI.submitDonationInHouse(DONATION_TYPE.IN_HOUSE.GOODS, payload)
      .then(() => {
        reset(defaultValues);
        setIsLoading(false);
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setIsLoading(false);
        alert(`Encountered an error. Please try again.${err}`);
      });
  };

  useEffect(() => {
    setValue(
      'donationAmount',
      projects.reduce((prev, val) => prev + val.amount, 0)
    );
  }, [projects]);

  return (
    <Stack>
      <Typography variant="body1">Donate brand new or used goods to this project.</Typography>

      <Box sx={(theme) => ({ p: 2, mt: 2, mb: 2, bgcolor: theme.palette.grey['200'], borderRadius: 1 })}>
        {channels.map((channel, idx) => (
          <Stack key={idx} spacing={1} direction="row" alignItems="center">
            <div>
              <Circle sx={{ width: 8, height: 8 }} />
            </div>
            <Typography sx={{ typography: { xs: 'body2', md: 'body1' } }}>{channel.text}</Typography>
          </Stack>
        ))}
      </Box>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={6}>
          <RHFTextField fullWidth label="First Name" name="firstName" />
        </Grid>
        <Grid item xs={12} md={6}>
          <RHFTextField fullWidth label="Last Name" name="lastName" />
        </Grid>
        <Grid item container xs={6} direction="column" alignItems="start">
          <RHFTextField fullWidth label="Email" name="email" />
          <Typography variant="caption" fontStyle={'italic'} color={'#aeaeae'}>
            We will send your donation receipt to the email address provided.
          </Typography>
        </Grid>
        <Grid item container xs={12} md={6} direction="column" alignItems="start">
          <RHFTextField fullWidth name="phone" label="Contact Number" />
          <Typography variant="caption" fontStyle={'italic'} color={'#aeaeae'}>
            We will contact you in case we encounter issues with your donation.
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <RHFTextField fullWidth label="Address" name="address" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={countries.find((c) => c.code === values.country) ?? ''}
            fullWidth
            onChange={(ev, e) => setValue('country', e.code)}
            renderInput={(e) => <TextField label="Country" {...e} />}
            options={countries}
            getOptionLabel={(e) => e?.label ?? ''}
            isOptionEqualToValue={(a, b) => a.code === b.code}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ color: 'text.disabled' }}>
          Items to be donated:
        </Typography>

        {/* <Button
          variant="contained"
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={addItem}
          sx={{ flexShrink: 0, boxShadow: 0 }}
        >
          Add new Items
        </Button> */}
      </Box>
      {values.items?.map((item, index) => (
        <Grid container spacing={2} key={item.id} mb={2}>
          <Grid item xs={12} md={item.goods === 'others, specify' ? 2 : 6}>
            <RHFSelect
              value={item.goods ?? ''}
              name={`items[${index}].goods`}
              label="Item type"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem
                value=""
                sx={{
                  mx: 1,
                  borderRadius: 0.75,
                  typography: 'body2',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                None
              </MenuItem>
              <Divider />
              {GOODS_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 0.75,
                    typography: 'body2',
                    textTransform: 'capitalize',
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </RHFSelect>
          </Grid>
          {item.goods === 'others, specify' && (
            <Grid item xs={12} md={4}>
              <RHFTextField name={`items[${index}].others`} label="Others" InputLabelProps={{ shrink: true }} />
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <RHFTextField
              type="number"
              value={item.quantity ?? ''}
              name={`items[${index}].quantity`}
              label="Quantity"
              onChange={(event) => {
                setValue(`items[${index}].quantity`, event.target.value ? parseFloat(event.target.value) : '');
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {index !== 0 && (
            <Grid item xs={12} md={1}>
              <Button
                color="error"
                startIcon={<Iconify icon="eva:trash-2-outline" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Grid>
          )}
        </Grid>
      ))}

      <Grid item xs={8} mb={5}>
        <ButtonBase
          focusRipple
          onClick={addItem}
          sx={{
            width: '100%',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1.5, // ~12px
            px: 2,
            py: 1.25,
            gap: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            bgcolor: 'background.paper',
            transition: (t) =>
              t.transitions.create(['background-color', 'border-color'], {
                duration: t.transitions.duration.shortest,
              }),
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'text.disabled',
            },
            '&:active': {
              bgcolor: 'action.selected',
            },
          }}
        >
          <AddRounded fontSize="small" />
          <Typography variant="body2">Add Item</Typography>
        </ButtonBase>
      </Grid>

      <UploadPhoto
        error={errors.file}
        name="file"
        label="Uploading photos of goods you want to donate helps us determine what we need to prepare for pick-up and will save you the time and effort of describing to us the goods you will be donating."
        selectedImages={selectedImages ?? []}
        setSelectedImages={setSelectedImages}
      />

      <Grid mt={4} container alignItems="center" gap={1.5}>
        <Grid item xs={2} md={0.5}>
          <Radio
            checked={!values.pickup}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                setValue('pickup', false);
                setValue('pickupAddress', '');
              }
            }}
          />
          {/* <RHFCheckbox name="pickup" label="" /> */}
        </Grid>
        <Grid item xs={10}>
          <Typography variant="body1" sx={{ mr: 2 }}>
            I will drop-off donation items in:
          </Typography>
        </Grid>
        {!values.pickup && (
          <Grid item xs={12} pl={5}>
            <RHFRadioGroup
              value={values.dropAddress ?? ''}
              name="dropAddress"
              options={options}
              sx={{
                '& .MuiFormControlLabel-root': { ml: 1 },
                '&': {
                  flexDirection: 'column',
                },
              }}
            />
          </Grid>
        )}
      </Grid>

      <Grid container alignItems="center" gap={1.5}>
        <Grid item xs={2} md={0.5}>
          <Radio
            checked={values.pickup === true}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                setValue('pickup', true);
                setValue('dropAddress', null);
              }
            }}
          />
          {/* <RHFCheckbox name="pickup" label="" /> */}
        </Grid>
        <Grid item xs={10}>
          <Typography variant="body1" sx={{ mr: 2 }}>
            I will request for pick-up of donation items.{' '}
            <Typography
              component={'span'}
              sx={{ display: 'inline-block', fontStyle: 'italic', color: '#aeaeae' }}
            >{`(Available in Metro Manila only.)`}</Typography>
          </Typography>
        </Grid>
        {values.pickup && (
          <Grid item xs={12} pl={5}>
            <Typography fontSize={16}>
              I will request for check pick-up in this address.{' '}
              <Typography component={'span'} sx={{ fontStyle: 'italic', color: '#aeaeae' }}>
                (If not the address you provided above.)
              </Typography>
            </Typography>
            <RHFTextField fullWidth name="pickupAddress" />
          </Grid>
        )}
        <Grid mt={2} container alignItems="center" gap={1.5}>
          <Grid item xs={2} md={0.5}>
            <Checkbox />
          </Grid>
          <Grid item xs={10}>
            <Typography fontSize={16}>I am interested to receive CharityPhilippines.org's email updates</Typography>
          </Grid>
        </Grid>
        <Stack spacing={2}>
          <Typography variant="body1" sx={{ mr: 2 }}>
            Where did you hear about CharityPhilippines.org?
          </Typography>

          <Select
            value={values.charitySource ?? '_'}
            onChange={(e) => setValue('charitySource', e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="_">Please Choose</MenuItem>
            <MenuItem value="Family or Friend">Family or Friend</MenuItem>
            <MenuItem value="Proactively Looking">Proactively Looking</MenuItem>
            <MenuItem value="Workplace">Workplace</MenuItem>
            <MenuItem value="Social Media">Social Media</MenuItem>
            <MenuItem value="Social Media Advertising">Social Media Advertising</MenuItem>
            <MenuItem value="Others">Others</MenuItem>
          </Select>

          {values.charitySource === 'Others' && (
            <Box width={220}>
              <RHFTextField name="charityOtherSource" label="Specify Other" />
            </Box>
          )}
        </Stack>
      </Grid>
      <NoAmountModal open={noAmountOpen} onClose={() => setNoAmountOpen(false)} />
    </Stack>
  );
}

const createPayload = async ({ discretionary, formData, images, projects, causes }) => {
  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    contactNumber: formData.phone,
    address: formData.address ? formData.address : ' ',
    country: formData.country,
    donationItems: formData.items.map((item) => ({
      goods: item.goods === 'others, specify' ? item.others : item.goods,
      quantity: item.quantity,
    })),
    images,
    file: images,
    imagesBase64: [],
    dropAddress: formData.dropAddress,
    pickup: formData.pickup ?? false,
    pickupAddress: formData.pickupAddress === '' ? formData.address : formData.pickupAddress,
    emailUpdate: formData.emailUpdate ?? false,
    charitySource: formData.charitySource === 'Others' ? formData.charityOtherSource : formData.charitySource,
    projects: projects.map((e) => ({ ...e, charityName: e.charityDetails.name, id: e._id })),
    discretion: {
      isDiscretion: discretionary,
      causes,
    },
  };
  payload.imagesBase64 = await convertImagesToBase64STring(payload.images);
  return payload;
};
